import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import UniversalHeader from './UniversalHeader';

/**
 * Enhanced Learning Path Dashboard with Mathematical Playground Design
 * Features: Asymmetric cards, geometric patterns, vibrant progress indicators
 * Maintains all existing functionality while improving visual engagement
 */

const EnhancedLearningPathDashboard = ({ onSelectTopic, onBackToDashboard }) => {
  const { user, userProfile } = useAuth();
  const [strands, setStrands] = useState([]);
  const [improvementAreas, setImprovementAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [learningPathData, setLearningPathData] = useState(null);
  const [userProgress, setUserProgress] = useState({});

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const userId = user?.id;
  const userGrade = userProfile?.grade || 'Year 4';

  // Function to calculate strand progress from user progress data
  const calculateStrandProgress = (strand, progressData) => {
    if (!strand.subtopics || strand.subtopics.length === 0) {
      return {
        completion: 0,
        totalTasks: 0,
        completedTasks: 0,
        totalSessions: 0,
        completedSessions: 0
      };
    }

    let totalTasks = 0;
    let completedTasks = 0;
    let totalSessions = 0;
    let completedSessions = 0;

    strand.subtopics.forEach(subtopic => {
      if (subtopic.tasks) {
        subtopic.tasks.forEach(task => {
          totalTasks++;

          const progressKey = `${subtopic.id}-${task.difficulty}`;
          const taskProgress = progressData[progressKey];

          if (taskProgress && taskProgress.completed_sessions > 0) {
            completedTasks++;
            totalSessions += taskProgress.total_sessions || 0;
            completedSessions += taskProgress.completed_sessions || 0;
          }
        });
      }
    });

    const completion = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

    return {
      completion,
      totalTasks,
      completedTasks,
      totalSessions,
      completedSessions
    };
  };

  // Function to get strand icon and color based on strand name
  const getStrandDisplayInfo = (strandName) => {
    const strandMap = {
      'Number and Algebra': { icon: '🔢', color: 'primary' },
      'Measurement and Geometry': { icon: '📐', color: 'primary' },
      'Statistics and Probability': { icon: '📈', color: 'primary' },
      'Fractions and Decimals': { icon: '🍕', color: 'primary' },
      'Patterns and Algebra': { icon: '📊', color: 'primary' }
    };

    const match = Object.keys(strandMap).find(key =>
      strandName.toLowerCase().includes(key.toLowerCase()) ||
      key.toLowerCase().includes(strandName.toLowerCase())
    );

    return match ? strandMap[match] : { icon: '📚', color: 'primary' };
  };

  const refreshDashboardData = async () => {
    console.log('🔄 Refreshing Learning Path Dashboard data...');
    await loadDashboardData();
  };

  useEffect(() => {
    window.refreshLearningPathDashboard = refreshDashboardData;
    return () => {
      delete window.refreshLearningPathDashboard;
    };
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      if (userId) {
        try {
          const statsResponse = await fetch(`${backendUrl}/api/test-history/${userId}`);
          if (statsResponse.ok) {
            const statsData = await statsResponse.json();
            if (statsData.success && statsData.statistics) {
              setUserStats(statsData.statistics);

              if (statsData.statistics.improvement_area && statsData.statistics.improvement_area !== 'Start with any topic') {
                setImprovementAreas([{
                  topic: statsData.statistics.improvement_area,
                  reason: 'Based on recent test performance',
                  priority: 'high'
                }]);
              }
            }
          }
        } catch (error) {
          console.error('Error fetching user stats:', error);
        }

        try {
          const learningPathsResponse = await fetch(`${backendUrl}/api/admin/learning-paths`);

          if (learningPathsResponse.ok) {
            const learningPathsData = await learningPathsResponse.json();

            if (learningPathsData.success && learningPathsData.data.length > 0) {
              const learningPath = learningPathsData.data.find(lp =>
                lp.year_level === userGrade.replace('Year ', '') ||
                lp.year_level === userGrade
              ) || learningPathsData.data[0];

              const previewResponse = await fetch(`${backendUrl}/api/admin/learning-paths/${learningPath.id}/preview?user_id=${userId}`);

              if (previewResponse.ok) {
                const previewData = await previewResponse.json();
                if (previewData.success && previewData.data.strands) {
                  setLearningPathData(previewData.data);

                  const progressResponse = await fetch(`${backendUrl}/api/learning-path/practice/user-progress/${userId}`);
                  let progressData = {};

                  if (progressResponse.ok) {
                    const progressResult = await progressResponse.json();

                    if (progressResult.success && progressResult.progress_summaries) {
                      progressResult.progress_summaries.forEach(progress => {
                        const key = `${progress.subtopic_id}-${progress.difficulty_level}`;
                        progressData[key] = {
                          completed_sessions: progress.completed_sessions || 0,
                          total_sessions: progress.total_sessions || 0,
                          average_score: progress.average_score || 0,
                          best_score: progress.best_score || 0
                        };
                      });
                    }
                  }

                  setUserProgress(progressData);

                  const strandsWithProgress = previewData.data.strands.map(strand => {
                    const strandProgress = calculateStrandProgress(strand, progressData);
                    const displayInfo = getStrandDisplayInfo(strand.strand_name);

                    return {
                      ...strand,
                      ...strandProgress,
                      ...displayInfo,
                      name: strand.strand_name,
                      description: `${strand.subtopics?.length || 0} topics, ${strandProgress.totalTasks} lessons available`
                    };
                  });

                  setStrands(strandsWithProgress);
                }
              }
            }
          }
        } catch (error) {
          console.error('Error loading learning path structure:', error);
          setError('Failed to load learning path data');
        }
      } else {
        setError('Please log in to view learning paths');
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setError('Failed to load learning path data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, [userId, userGrade, backendUrl]);

  if (loading) {
    return (
      <div className="mc-body" style={{ background: 'var(--mc-gray-100)', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="text-center">
          <div className="mc-progress-circle" style={{ width: '120px', height: '120px', margin: '0 auto 2rem' }} />
          <p className="mc-heading-3" style={{ color: 'var(--mc-gray-600)' }}>
            Loading your learning paths...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mc-body" style={{ background: 'var(--mc-gray-100)', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="mc-card text-center" style={{ maxWidth: '500px' }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>⚠️</div>
          <h2 className="mc-heading-1" style={{ color: 'var(--mc-error)', marginBottom: '1rem' }}>
            Something went wrong
          </h2>
          <p className="mc-body" style={{ color: 'var(--mc-gray-600)', marginBottom: '1.5rem' }}>
            {error}
          </p>
          <button onClick={onBackToDashboard} className="mc-button mc-button-primary">
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mc-body" style={{ background: 'var(--mc-gray-100)', minHeight: '100vh', position: 'relative' }}>

      <UniversalHeader
        currentPage="learning-paths"
        onBackToDashboard={onBackToDashboard}
        breadcrumbs={[
          { label: 'Dashboard', action: onBackToDashboard, current: false },
          { label: 'Learning Paths', action: null, current: true }
        ]}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" style={{ position: 'relative', zIndex: 1 }}>
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="mc-display-1" style={{ color: 'var(--mc-charcoal)', marginBottom: '1rem' }}>
            Your Learning Journey
          </h1>
          <p className="mc-body-large" style={{ color: 'var(--mc-gray-600)' }}>
            Choose a topic to continue your math adventure for {userGrade}
          </p>
        </div>

        {/* Improvement Areas Section */}
        {improvementAreas.length > 0 ? (
          <div className="mb-12">
            <h2 className="mc-heading-1" style={{ color: 'var(--mc-charcoal)', marginBottom: '1.5rem' }}>
              Improvement Areas
            </h2>
            <div className="mc-dashboard-grid">
              {improvementAreas.map((area, index) => (
                <div key={index} className="mc-card mc-card-primary" style={{
                  background: 'var(--mc-primary-lighter)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      background: 'var(--mc-primary-light)',
                      borderRadius: 'var(--radius-md)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <span style={{ fontSize: '1.5rem' }}>🎯</span>
                    </div>
                    <h3 className="mc-heading-2" style={{ color: 'var(--mc-primary)', marginBottom: 0 }}>
                      {area.topic}
                    </h3>
                  </div>
                  <p className="mc-body" style={{ color: 'var(--mc-gray-600)', marginBottom: '1rem' }}>
                    {area.reason}
                  </p>
                  <span className="mc-badge mc-badge-primary">
                    {area.priority} priority
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="mb-12">
            <div className="mc-card text-center" style={{ border: '2px dashed var(--mc-gray-200)' }}>
              <span style={{ fontSize: '3rem', display: 'block', marginBottom: '1rem' }}>📊</span>
              <h3 className="mc-heading-2" style={{ color: 'var(--mc-gray-600)', marginBottom: '0.75rem' }}>
                Not Enough Data
              </h3>
              <p className="mc-body" style={{ color: 'var(--mc-gray-600)', marginBottom: '1.5rem' }}>
                Take an evaluation test to get personalized improvement insights
              </p>
              <button onClick={onBackToDashboard} className="mc-button mc-button-primary">
                Take Evaluation Test
              </button>
            </div>
          </div>
        )}

        {/* Learning Path Strands Grid */}
        <div className="mb-12">
          <h2 className="mc-heading-1" style={{ color: 'var(--mc-charcoal)', marginBottom: '1.5rem' }}>
            Learning Path Strands
          </h2>
          <div className="mc-dashboard-grid">
            {strands.map((strand, index) => {
              return (
                <div
                  key={strand.id}
                  className="mc-card mc-card-interactive"
                  onClick={() => onSelectTopic(strand)}
                  style={{
                    cursor: 'pointer'
                  }}
                >
                  <div className="text-center">
                    <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>{strand.icon}</div>
                    <h3 className="mc-heading-2" style={{ marginBottom: '0.75rem' }}>
                      {strand.name}
                    </h3>
                    <p className="mc-body-small" style={{ color: 'var(--mc-gray-600)', marginBottom: '1.5rem' }}>
                      {strand.description}
                    </p>

                    {/* Progress Bar */}
                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: '0.5rem'
                      }}>
                        <span className="mc-caption" style={{ color: 'var(--mc-gray-600)' }}>
                          PROGRESS
                        </span>
                        <span className="mc-heading-3" style={{ color: 'var(--mc-primary)', marginBottom: 0 }}>
                          {strand.completion}%
                        </span>
                      </div>
                      <div className="mc-progress-bar">
                        <div
                          className="mc-progress-fill"
                          style={{
                            width: `${strand.completion}%`
                          }}
                        />
                      </div>
                    </div>

                    {/* Task Count */}
                    <div className="mc-body-small" style={{ color: 'var(--mc-gray-600)', marginBottom: '1.5rem' }}>
                      <span style={{ fontWeight: 600 }}>{strand.completedTasks}</span> of{' '}
                      <span style={{ fontWeight: 600 }}>{strand.totalTasks}</span> lessons completed
                    </div>

                    {/* Action Button */}
                    <button
                      className="mc-button mc-button-primary mc-button-large w-full"
                    >
                      {strand.completion > 0 ? 'Continue Learning' : 'Start Learning'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Quick Stats */}
        {userStats && (
          <div className="mc-card mc-card-primary" style={{
            background: 'var(--mc-primary-lighter)'
          }}>
            <h3 className="mc-heading-2" style={{ color: 'var(--mc-primary)', marginBottom: '1.5rem' }}>
              Your Learning Stats
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="mc-score-display" style={{ marginBottom: '0.5rem' }}>
                  {userStats.total_tests || 0}
                </div>
                <div className="mc-body" style={{ color: 'var(--mc-slate-gray)' }}>Tests Completed</div>
              </div>
              <div className="text-center">
                <div className="mc-score-display" style={{ marginBottom: '0.5rem' }}>
                  {userStats.average_score || 0}%
                </div>
                <div className="mc-body" style={{ color: 'var(--mc-slate-gray)' }}>Average Score</div>
              </div>
              <div className="text-center">
                <div className="mc-score-display" style={{ marginBottom: '0.5rem' }}>
                  {strands.reduce((acc, strand) => acc + (strand.completedTasks || 0), 0)}
                </div>
                <div className="mc-body" style={{ color: 'var(--mc-slate-gray)' }}>Lessons Completed</div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default EnhancedLearningPathDashboard;
